import { useState } from "react";
import { useAtom, useAtomValue } from "jotai";
import { Center, Text3D } from "@react-three/drei";
import { 
  portraitLobbySelectionAtom, 
  audioVolumeAtom, 
  languageAtom,
  deviceAtom
} from "../../GlobalState.jsx";
import useSoundEffectsPlayer from "../../soundPlayers/useSoundEffectsPlayer.jsx";
import MeshColors from "../MeshColors.jsx";
import translations from "../../dictionaries/translations.js";
import PlayersParty from "./PlayersParty.jsx";
import LobbySettings from "../LobbySettings.jsx";
import HowToPlay from "../HowToPlay.jsx";
import { trackEvent } from "../../api";

export default function BodySection({ position, scale }) {
  const [selection, setSelection] = useAtom(portraitLobbySelectionAtom)
  const language = useAtomValue(languageAtom)
  const device = useAtomValue(deviceAtom)
  const { playSoundEffect } = useSoundEffectsPlayer()

  function SettingsButton({ position, scale }) {
    const [hover, setHover] = useState(false)
    const audioVolume = useAtomValue(audioVolumeAtom)
    function handlePointerEnter(e) {
      e.stopPropagation()
      setHover(true)
    }
    function handlePointerLeave(e) {
      e.stopPropagation()
      setHover(false)
    }
    function handlePointerDown(e) {
      e.stopPropagation()
      setSelection('settings')
      trackEvent({ surface: 'lobby', action: 'open_settings', category: 'ui' })
      playSoundEffect('/sounds/effects/button-click.mp3', audioVolume)
    }
    return <group name='settings-button' position={position} scale={scale}>
      <mesh name='background-outer' scale={[2.8, 0.01, 0.75]}>
        <boxGeometry args={[1, 1, 1]}/>
        <meshStandardMaterial color={ (hover || selection === 'settings') ? 'green' : 'orange' }/>
      </mesh> 
      <mesh name='background-inner' scale={[2.75, 0.02, 0.7]}>
        <boxGeometry args={[1, 1, 1]}/>
        <meshStandardMaterial color={MeshColors.spaceDark}/>
      </mesh>
      <mesh 
      name='wrapper' 
      scale={[2.8, 0.02, 0.75]}
      onPointerEnter={e => handlePointerEnter(e)}
      onPointerLeave={e => handlePointerLeave(e)}
      onPointerDown={e => handlePointerDown(e)}>
        <boxGeometry args={[1, 1, 1]}/>
        <meshStandardMaterial color='orange' transparent opacity={0}/>
      </mesh>
      <Center position={[0, 0.02, 0]} rotation={[-Math.PI/2, 0, 0]}>
        <Text3D
          font={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"}
          size={0.4}
          height={0.01}
        >
          {translations.lobby.settings[language]}
          <meshStandardMaterial color={ (hover || selection === 'settings') ? 'green' : 'orange' }/>
        </Text3D>
      </Center>
    </group>
  }
  function PlayersButton({ position, scale }) {
    const [hover, setHover] = useState(false)
    const audioVolume = useAtomValue(audioVolumeAtom)
    function handlePointerEnter(e) {
      e.stopPropagation()
      setHover(true)
    }
    function handlePointerLeave(e) {
      e.stopPropagation()
      setHover(false)
    }
    function handlePointerDown(e) {
      e.stopPropagation()
      setSelection('players')
      playSoundEffect('/sounds/effects/button-click.mp3', audioVolume)
    }
    return <group name='settings-button' position={position} scale={scale}>
      <mesh name='background-outer' scale={[2.55, 0.01, 0.75]}>
        <boxGeometry args={[1, 1, 1]}/>
        <meshStandardMaterial color={ (hover || selection === 'players') ? 'green' : 'orange' }/>
      </mesh> 
      <mesh name='background-inner' scale={[2.5, 0.02, 0.7]}>
        <boxGeometry args={[1, 1, 1]}/>
        <meshStandardMaterial color={MeshColors.spaceDark}/>
      </mesh>
      <mesh 
      name='wrapper' 
      scale={[2.55, 0.02, 0.75]}
      onPointerEnter={e => handlePointerEnter(e)}
      onPointerLeave={e => handlePointerLeave(e)}
      onPointerDown={e => handlePointerDown(e)}>
        <boxGeometry args={[1, 1, 1]}/>
        <meshStandardMaterial color='orange' transparent opacity={0}/>
      </mesh>
      <Center position={[0, 0.02, 0]} rotation={[-Math.PI/2, 0, 0]}>
        <Text3D
          font={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"}
          size={0.4}
          height={0.01}
        >
          {translations.lobby.players[language]}
          <meshStandardMaterial color={ (hover || selection === 'players') ? 'green' : 'orange' }/>
        </Text3D>
      </Center>
    </group>
  }
  function RulebookButton({ position, scale }) {
    const [hover, setHover] = useState(false)
    const audioVolume = useAtomValue(audioVolumeAtom)
    function handlePointerEnter(e) {
      e.stopPropagation()
      setHover(true)
    }
    function handlePointerLeave(e) {
      e.stopPropagation()
      setHover(false)
    }
    function handlePointerDown(e) {
      e.stopPropagation()
      setSelection('rulebook')
      playSoundEffect('/sounds/effects/button-click.mp3', audioVolume)
    }
    return <group name='settings-button' position={position} scale={scale}>
      <mesh name='background-outer' scale={[2.95, 0.01, 0.75]}>
        <boxGeometry args={[1, 1, 1]}/>
        <meshStandardMaterial color={ (hover || selection === 'rulebook') ? 'green' : 'orange' }/>
      </mesh> 
      <mesh name='background-inner' scale={[2.9, 0.02, 0.7]}>
        <boxGeometry args={[1, 1, 1]}/>
        <meshStandardMaterial color={MeshColors.spaceDark}/>
      </mesh>
      <mesh 
      name='wrapper' 
      scale={[2.85, 0.02, 0.75]}
      onPointerEnter={e => handlePointerEnter(e)}
      onPointerLeave={e => handlePointerLeave(e)}
      onPointerDown={e => handlePointerDown(e)}>
        <boxGeometry args={[1, 1, 1]}/>
        <meshStandardMaterial color='orange' transparent opacity={0}/>
      </mesh>
      <Center position={[0, 0.02, 0]} rotation={[-Math.PI/2, 0, 0]}>
        <Text3D
          font={language === 'korean' ? "/fonts/Maplestory_Bold.json" : language === 'chinese' ? "/fonts/Xiao Xiangjiao font_Regular.json" : "/fonts/Luckiest Guy_Regular.json"}
          size={0.4}
          height={0.01}
        >
          {translations.lobby.rulebook[language]}
          <meshStandardMaterial color={ (hover || selection === 'rulebook') ? 'green' : 'orange' }/>
        </Text3D>
      </Center>
    </group>
  }
  return <group position={position} scale={scale}>
    <SettingsButton position={[-3.85, 0, -7]} scale={1.3}/>
    <PlayersButton position={[-0.1,0,-7]} scale={1.3}/>
    <RulebookButton position={[3.8,0,-7]} scale={1.3}/>
    { selection === 'players' && <PlayersParty position={[0, 0, 0.5]}/> }
    { selection === 'settings' && <LobbySettings position={[-9,0,-1.7]} ruleWidth={8.4}/> }
    { selection === 'rulebook' && <HowToPlay device={device} scale={0.75} position={[-2.3, 0, -1.6]}/> }
  </group>
}
